import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearch } from 'wouter';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth-store';
import { getJson } from '@/lib/http';
import { canPublishPhotoChecks, canEditPhotoChecks, canAccessPhotoChecksAdvanced, canMakePhotoChecksLive, type PhotoChecksMeta } from '@/lib/photoChecksAccess';
import { apiRequest } from '@/lib/queryClient';

type Comparison = {
  id: number;
  snapshotId: number;
  status: string;
  metricsSummary: Record<string, any> | null;
  baselinePromptConfigId: number | null;
  candidatePromptConfigId: number | null;
  completedAt: string | null;
  createdAt: string | null;
  errorMessage: string | null;
};

type PromptConfig = {
  id: number;
  name: string;
  version: number;
  status: string;
  isActive: boolean;
};

function decisionFromSummary(summary: Record<string, any> | null | undefined): {
  verdict: 'safe' | 'unsafe' | 'inconclusive' | 'none';
  title: string;
  detail: string;
  lines: string[];
} {
  if (!summary?.baseline || !summary?.candidate) {
    return {
      verdict: 'none',
      title: 'No trial decision yet',
      detail: 'Run a safe trial under Advanced when you have a proposed change and a locked set of reviewed photos.',
      lines: [],
    };
  }

  const safety = String(summary.overallSafetyStatus ?? 'unknown');
  const b = summary.baseline;
  const c = summary.candidate;
  const bMiss = Number(b.falseClearCount ?? 0);
  const cMiss = Number(c.falseClearCount ?? 0);
  const bDen = Number(b.successfullyEvaluatedGoldRejects ?? 0);
  const cDen = Number(c.successfullyEvaluatedGoldRejects ?? 0);

  const lines = [
    `Proposed change missed ${cMiss} of ${cDen} rejected photos we could score.`,
    `Live check missed ${bMiss} of ${bDen}.`,
  ];

  if (safety === 'improved') {
    return {
      verdict: 'safe',
      title: 'Yes — safer to make live on rejected photos missed',
      detail: 'On this locked test set, the proposed change missed fewer rejected photos than the live check.',
      lines,
    };
  }
  if (safety === 'regressed') {
    return {
      verdict: 'unsafe',
      title: 'No — do not make this live',
      detail: 'On this locked test set, the proposed change missed more rejected photos than the live check.',
      lines,
    };
  }
  if (safety === 'unchanged') {
    return {
      verdict: 'inconclusive',
      title: 'About the same — no clear safety gain',
      detail: 'Rejected-photo misses look similar to the live check. Decide based on other trade-offs, or run another trial.',
      lines,
    };
  }
  return {
    verdict: 'inconclusive',
    title: 'Not enough to decide',
    detail: 'The trial did not produce a clear safety answer (for example coverage fell). Do not treat this as approval to go live.',
    lines,
  };
}

/**
 * Product question: Is it safe to make this live?
 * Trial preparation machinery lives at /admin/ai/eval (Advanced).
 */
export default function PhotoChecksResultsPage() {
  const { state } = useAuth();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const queryClient = useQueryClient();
  const canPublish = canPublishPhotoChecks(state.user);
  const canPrepareTrial = canEditPhotoChecks(state.user) || canAccessPhotoChecksAdvanced(state.user);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const metaQ = useQuery({
    queryKey: ['/api/admin/ai-eval/meta'],
    queryFn: () => getJson<PhotoChecksMeta>('/api/admin/ai-eval/meta'),
  });
  const canMakeLive = canMakePhotoChecksLive(state.user, metaQ.data?.livePublishEnabled);

  const trialParam = useMemo(() => {
    const params = new URLSearchParams(searchString.startsWith('?') ? searchString : `?${searchString}`);
    const trial = params.get('trial');
    return trial && /^\d+$/.test(trial) ? Number(trial) : null;
  }, [searchString]);

  const comparisonsQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/comparisons'],
    queryFn: () => getJson<{ ok: true; comparisons: Comparison[] }>('/api/admin/ai-eval/comparisons?limit=30'),
  });

  const draftsQuery = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'POSTING'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=POSTING'),
  });

  const comparisons = comparisonsQuery.data?.comparisons ?? [];
  const focus = (trialParam
    ? comparisons.find((c) => c.id === trialParam)
    : null)
    ?? comparisons.find((c) => c.status === 'completed')
    ?? comparisons[0]
    ?? null;

  const decision = decisionFromSummary(focus?.status === 'completed' ? focus.metricsSummary : null);
  const drafts = (draftsQuery.data?.prompts ?? []).filter((p) => p.status === 'DRAFT');
  const live = (draftsQuery.data?.prompts ?? []).find((p) => p.isActive && p.status === 'ACTIVE') ?? null;

  const makeLiveMutation = useMutation({
    mutationFn: async (promptId: number) => {
      setBusyId(promptId);
      setMessage(null);
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${promptId}/publish`, 'POST', {});
      return res.json();
    },
    onSuccess: () => {
      setMessage('Change is now live. Human review decisions are unchanged.');
      queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-triage/prompts'] });
    },
    onError: (err: Error) => setMessage(err.message),
    onSettled: () => setBusyId(null),
  });

  const VerdictIcon = decision.verdict === 'safe'
    ? CheckCircle2
    : decision.verdict === 'unsafe'
      ? AlertTriangle
      : HelpCircle;

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Photo Checks</p>
            <h1 className="text-2xl font-semibold">Is it safe to make this live?</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => setLocation('/admin/ai')}>
            Home
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          The decision for a proposed change, based on a locked set of reviewed photos.
          A trial never makes anything live by itself.
        </p>
      </div>

      {!live && (
        <Alert variant="destructive">
          <AlertTitle>No live photo standard</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>There is nothing live to compare against yet.</p>
            <Button size="sm" onClick={() => setLocation('/admin/ai/checks')}>
              Photo standards
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {message && (
        <Alert>
          <AlertTitle>Update</AlertTitle>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      )}

      <Card className={
        decision.verdict === 'unsafe'
          ? 'border-red-300'
          : decision.verdict === 'safe'
            ? 'border-emerald-300'
            : undefined
      }
      >
        <CardHeader>
          <CardTitle className="flex items-start gap-2 text-xl">
            <VerdictIcon className={`h-5 w-5 mt-0.5 shrink-0 ${
              decision.verdict === 'safe'
                ? 'text-emerald-700'
                : decision.verdict === 'unsafe'
                  ? 'text-red-700'
                  : 'text-muted-foreground'
            }`}
            />
            {decision.title}
          </CardTitle>
          <CardDescription>{decision.detail}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {focus && (
            <p className="text-muted-foreground">
              Trial #{focus.id}
              {focus.status !== 'completed' ? ` · ${focus.status}` : ''}
              {focus.completedAt ? ` · ${new Date(focus.completedAt).toLocaleString()}` : ''}
            </p>
          )}
          {decision.lines.length > 0 && (
            <ul className="space-y-1 font-medium">
              {decision.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}

          {canMakeLive
            && focus?.candidatePromptConfigId
            && decision.verdict === 'safe'
            && (
              <Button
                disabled={busyId === focus.candidatePromptConfigId || makeLiveMutation.isPending}
                onClick={() => {
                  const id = Number(focus.candidatePromptConfigId);
                  const ok = window.confirm(
                    'Make this proposed change live?\n\n'
                    + 'This replaces the current live photo standard. '
                    + 'It does not change human review decisions.',
                  );
                  if (ok) makeLiveMutation.mutate(id);
                }}
              >
                Make live
              </Button>
            )}
          {canPublish && !canMakeLive && (
            <p className="text-sm text-muted-foreground">
              Live publish is off for now — keep using trials here until you flip the switch.
            </p>
          )}

          {decision.verdict === 'unsafe' && (
            <p className="text-sm text-red-800">
              Keep the current live standard. Revise the proposed change and try again.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Proposed changes</CardTitle>
          <CardDescription>
            Drafts waiting for a safety decision. Preparing or re-running trials is an Advanced step.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {drafts.length === 0 && (
            <p className="text-muted-foreground">
              No proposed changes. Propose one from Photo standards when you want to change what we check.
            </p>
          )}
          {drafts.map((draft) => (
            <div key={draft.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-3">
              <div>
                <div className="font-medium">{draft.name} v{draft.version}</div>
                <div className="text-xs text-muted-foreground">Draft · not live</div>
              </div>
              <Badge variant="secondary">Proposed</Badge>
            </div>
          ))}
          {canPrepareTrial && (
            <Button variant="outline" size="sm" onClick={() => setLocation('/admin/ai/eval')}>
              Prepare or re-run a trial
            </Button>
          )}
        </CardContent>
      </Card>

      {comparisons.filter((c) => c.status === 'completed').length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Earlier decisions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {comparisons.filter((c) => c.status === 'completed').slice(0, 8).map((c) => {
              const d = decisionFromSummary(c.metricsSummary);
              return (
                <button
                  key={c.id}
                  type="button"
                  className="w-full text-left rounded border px-3 py-2 hover:bg-muted/40"
                  onClick={() => setLocation(`/admin/ai/results?trial=${c.id}`)}
                >
                  <div className="font-medium">Trial #{c.id} — {d.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.completedAt ? new Date(c.completedAt).toLocaleString() : 'Completed'}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
